import React, { useEffect, useState, useMemo } from 'react';
import { getAiSignalHistory } from '../../services/OrdersService';

/**
 * Painel "Acurácia por IA" com filtros. Agrega aiSignals avaliados por
 * (provider, model) e mostra acurácia comparativa. Fecha o loop com a
 * Fase 3 (multi-IA): trader vê qual provedor está acertando mais nos
 * sinais que deu, com filtros pra investigar nichos (ex: "acurácia em
 * BTCUSDT só com confiança ≥ 70%").
 *
 * Self-fetching: chama getAiSignalHistory(filterSymbol, 200) e agrega
 * localmente. Recarrega quando filterSymbol muda.
 *
 * Regime ficou como follow-up — aiSignals não tem coluna de regime
 * gravada. Iteração futura pode adicionar via migration.
 */
function AiAccuracyByProvider() {
    const [signals, setSignals] = useState([]);
    const [loading, setLoading] = useState(true);
    const [filterSymbol, setFilterSymbol] = useState('');
    const [minConfidence, setMinConfidence] = useState(0);

    useEffect(() => {
        setLoading(true);
        getAiSignalHistory(filterSymbol || null, 200)
            .then(rows => { setSignals(rows || []); setLoading(false); })
            .catch(() => { setSignals([]); setLoading(false); });
    }, [filterSymbol]);

    // Símbolos disponíveis: derivados dos signals carregados (quando sem
    // filtro de symbol). Quando há filtro de symbol, mantém o último set
    // visto pra continuar oferecendo as opções.
    const [availableSymbols, setAvailableSymbols] = useState([]);
    useEffect(() => {
        if (!filterSymbol && signals.length > 0) {
            const uniq = Array.from(new Set(signals.map(s => s.symbol))).sort();
            setAvailableSymbols(uniq);
        }
    }, [signals, filterSymbol]);

    // Aggregação local por (provider, model), aplicando filtro de confidence
    const byProvider = useMemo(() => {
        const filtered = signals.filter(s => {
            if (s.outcome === null || s.outcome === undefined) return false;
            const conf = parseFloat(s.confidence) || 0;
            return conf * 100 >= minConfidence;
        });

        const originLabel = d => (d === null || d === undefined) ? '(legado)' : (d === 0 ? 'primary' : `fallback #${d}`);

        const buckets = {};
        for (const s of filtered) {
            const provider = s.provider || '(legacy)';
            const model = s.model || 'default';
            const origin = originLabel(s.fallbackDepth);
            const key = `${provider}|${model}|${origin}`;
            if (!buckets[key]) buckets[key] = {
                provider, model, origin,
                total: 0, correct: 0, incorrect: 0, neutral: 0
            };
            buckets[key].total++;
            if (s.outcome === 'CORRECT') buckets[key].correct++;
            else if (s.outcome === 'INCORRECT') buckets[key].incorrect++;
            else buckets[key].neutral++;
        }

        return Object.values(buckets)
            .map(p => ({ ...p, accuracy: parseFloat(((p.correct / p.total) * 100).toFixed(1)) }))
            .sort((a, b) => b.accuracy - a.accuracy);
    }, [signals, minConfidence]);

    const totalFiltered = byProvider.reduce((s, p) => s + p.total, 0);

    return (
        <div className='card border-0 shadow mb-4'>
            <div className='card-header'>
                <h6 className='mb-1'>🤖 Acurácia por IA</h6>
                <small className='text-muted'>
                    Compara o desempenho de cada provedor/modelo nos sinais avaliados.
                </small>

                {/* Filtros */}
                <div className='row mt-2 g-2 align-items-end'>
                    <div className='col-md-4'>
                        <label className='form-label small mb-1 text-muted'>Símbolo</label>
                        <select
                            className='form-select form-select-sm'
                            value={filterSymbol}
                            onChange={e => setFilterSymbol(e.target.value)}>
                            <option value=''>Todos os símbolos</option>
                            {availableSymbols.map(s => (
                                <option key={s} value={s}>{s}</option>
                            ))}
                        </select>
                    </div>
                    <div className='col-md-5'>
                        <label className='form-label small mb-1 text-muted'>
                            Confiança mínima: <span className='text-primary'>{minConfidence}%</span>
                        </label>
                        <input
                            type='range'
                            className='form-range'
                            min='0' max='95' step='5'
                            value={minConfidence}
                            onChange={e => setMinConfidence(parseInt(e.target.value))}
                        />
                    </div>
                    <div className='col-md-3'>
                        <label className='form-label small mb-1 text-muted' title='Regime do mercado naquele momento'>
                            Regime
                        </label>
                        <select
                            className='form-select form-select-sm'
                            disabled
                            title='Em breve — aiSignals precisa ganhar campo de regime'>
                            <option>Todos (em breve)</option>
                        </select>
                    </div>
                </div>
            </div>

            {loading && (
                <div className='card-body text-muted text-center py-3' style={{ fontSize: 13 }}>
                    Carregando...
                </div>
            )}

            {!loading && totalFiltered === 0 && (
                <div className='card-body text-muted py-3' style={{ fontSize: 13 }}>
                    Nenhum sinal avaliado com os filtros atuais.
                    {minConfidence > 0 && ' Tente reduzir a confiança mínima.'}
                </div>
            )}

            {!loading && totalFiltered > 0 && (
                <>
                    <div className='table-responsive'>
                        <table className='table table-sm table-hover mb-0 align-middle'>
                            <thead className='thead-light'>
                                <tr className="align-middle">
                                    <th>Provedor</th>
                                    <th>Modelo</th>
                                    <th>Origem</th>
                                    <th className='text-end'>Sinais</th>
                                    <th className='text-end'>Acertou</th>
                                    <th className='text-end'>Errou</th>
                                    <th className='text-end'>Neutro</th>
                                    <th className='text-end'>Acurácia</th>
                                </tr>
                            </thead>
                            <tbody>
                                {byProvider.map((p, i) => {
                                    const acc = p.accuracy;
                                    const accCls = acc >= 60 ? 'text-success fw-bold'
                                        : acc >= 40 ? 'text-warning fw-bold'
                                        : 'text-danger fw-bold';
                                    return (
                                        <tr key={`${p.provider}-${p.model}-${p.origin}-${i}`}>
                                            <td><strong>{p.provider}</strong></td>
                                            <td><small className='text-muted'>{p.model}</small></td>
                                            <td>
                                                <span className={`badge ${p.origin === 'primary' ? 'bg-success' : p.origin === '(legado)' ? 'bg-secondary' : 'bg-warning text-dark'}`}>
                                                    {p.origin}
                                                </span>
                                            </td>
                                            <td className='text-end'>{p.total}</td>
                                            <td className='text-end text-success'>{p.correct}</td>
                                            <td className='text-end text-danger'>{p.incorrect}</td>
                                            <td className='text-end text-muted'>{p.neutral}</td>
                                            <td className={`text-end ${accCls}`}>{acc}%</td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>
                    <div className='card-footer text-muted' style={{ fontSize: 12 }}>
                        💡 Use os filtros pra investigar nichos: "acurácia em BTCUSDT só com confiança ≥ 70%".
                        Sinais com confiança alta tendem a ser mais decisivos — útil pra calibrar threshold das automações.
                    </div>
                </>
            )}
        </div>
    );
}

export default AiAccuracyByProvider;
